/**
 * OrderingPacketContext — state + actions for manual vendor ordering packets.
 *
 * Persists to DB (ordering_packets + ordering_vendor_lead_times).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";
import { useRole } from "@/data/RoleContext";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { Job } from "@/data/types";
import { getJobBom } from "@/data/jobBomSelector";
import { SEED_SKU_CATALOG } from "@/data/skuMapping";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";
import {
  type OrderingPacket, type OrderingLineItem, type OrderingAuditEvent,
  type OrderingPacketStatus, type OrderingLineStatus, type OrderingPacketOrderType,
  type OrderingPacketPaymentMethod, type VendorLeadTimeConfig,
  type VendorReceipt,
  type PurchaseOrder, type PurchaseOrderStatus, type POInvoiceLink,
} from "./types";

const uid = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

const isUuidLike = (value: string | undefined | null): boolean =>
  !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());

const normalizeVendorCandidate = (value: string | undefined | null): string => {
  const v = String(value ?? "").trim();
  if (!v) return "";
  if (v.toLowerCase() === "unassigned") return "";
  return v;
};

const inferVendorFromSkuOrName = (skuValue: string, nameValue: string): string => {
  const sku = String(skuValue || "").toUpperCase();
  const name = String(nameValue || "").toLowerCase();
  if (sku.includes("SEN")) return "Sentrel";
  if (name.includes("dekton")) return "Cosentino";
  if (name.includes("tile") || name.includes("daltile")) return "Daltile";
  if (
    name.includes("fixture") ||
    name.includes("grab") ||
    name.includes("rod") ||
    name.includes("drain") ||
    name.includes("adhesive") ||
    name.includes("silicone")
  ) {
    return "Home Depot";
  }
  return "";
};

const resolveEffectiveVendor = (
  line: Pick<OrderingLineItem, "actualVendor" | "projectedVendor" | "actualSku" | "projectedSku" | "actualItemName" | "projectedItemName">,
  intendedVendor: string | undefined | null,
): string => {
  const actual = normalizeVendorCandidate(line.actualVendor);
  if (actual) return actual;
  const projected = normalizeVendorCandidate(line.projectedVendor);
  if (projected) return projected;
  const intended = normalizeVendorCandidate(intendedVendor);
  if (intended) return intended;
  const inferred = inferVendorFromSkuOrName(
    String(line.actualSku ?? line.projectedSku ?? ""),
    String(line.actualItemName ?? line.projectedItemName ?? ""),
  );
  return inferred || "Unassigned";
};

const computeLineCost = (line: OrderingLineItem): number => {
  const base = (line.actualQty ?? line.projectedQty) * (line.actualUnitPrice ?? 0);
  return base + (line.actualTax ?? 0) + (line.actualShipping ?? 0) + (line.actualFees ?? 0);
};

const recomputeTotals = (packet: OrderingPacket): OrderingPacket => {
  const projectedTotal = packet.lineItems.reduce((s, l) => s + l.projectedLineCost, 0);
  const actualTotal = packet.lineItems.reduce((s, l) => {
    const lc = computeLineCost(l);
    return s + (l.actualUnitPrice != null ? lc : 0);
  }, 0);
  const exceptionCount = packet.lineItems.reduce(
    (n, l) => n + l.exceptions.filter((e) => !e.resolved).length, 0,
  );
  return { ...packet, projectedTotal, actualTotal, exceptionCount };
};

interface CreatePacketInput {
  job: Job;
  intendedVendor: string;
  orderType: OrderingPacketOrderType;
  notes: string;
  sentBy: string;
}

interface OrderingPacketContextValue {
  packets: OrderingPacket[];
  vendorLeadTimes: VendorLeadTimeConfig[];

  getPacket: (id: string) => OrderingPacket | undefined;
  getPacketsByJob: (jobId: string) => OrderingPacket[];
  getPacketsByStatus: (status: OrderingPacketStatus) => OrderingPacket[];

  createPacket: (input: CreatePacketInput) => OrderingPacket;
  updatePacketStatus: (id: string, status: OrderingPacketStatus, reason?: string) => void;
  updatePacketMeta: (id: string, patch: Partial<Pick<OrderingPacket,
    "assignedTo" | "vendorOrderNumber" | "paymentMethod" | "paymentTerms" | "expectedDeliveryDate" | "notes"
  >>) => void;

  updateLineItem: (
    packetId: string,
    lineId: string,
    patch: Partial<OrderingLineItem>,
    auditOptions?: { reason?: string; isOverride?: boolean },
  ) => void;
  setLineStatus: (packetId: string, lineId: string, status: OrderingLineStatus, reason?: string) => void;
  addLineException: (packetId: string, lineId: string, kind: import("./types").OrderingLineExceptionKind, detail: string) => void;
  resolveLineException: (packetId: string, lineId: string, exceptionIndex: number) => void;

  markPlaced: (packetId: string) => void;
  markReceived: (packetId: string) => void;
  cancelPacket: (packetId: string, reason: string) => void;

  // Vendor receipts (reconciliation)
  createReceipt: (packetId: string, init: Partial<Pick<VendorReceipt, "vendorName" | "invoiceNumber" | "invoiceDate" | "invoiceTotal">>) => VendorReceipt | undefined;
  updateReceipt: (packetId: string, receiptId: string, patch: Partial<Omit<VendorReceipt, "id" | "packetId" | "createdAt" | "createdBy" | "locked" | "lockedAt" | "lockedBy">>) => void;
  assignLineToReceipt: (packetId: string, lineId: string, receiptId: string | null) => void;
  lockReceipt: (packetId: string, receiptId: string) => void;
  unlockReceipt: (packetId: string, receiptId: string, reason: string) => void;
  deleteReceipt: (packetId: string, receiptId: string) => void;

  // Purchase Orders (formal company POs — one per vendor per packet)
  /** Generate POs for the packet, grouped by line.actualVendor (or projectedVendor if not yet set). One PO per vendor. Idempotent: existing POs are reused for matching vendors. */
  generatePOsForPacket: (packetId: string) => PurchaseOrder[];
  getAllPurchaseOrders: () => PurchaseOrder[];
  getPurchaseOrder: (poId: string) => { po: PurchaseOrder; packet: OrderingPacket } | undefined;
  updatePOStatus: (packetId: string, poId: string, status: PurchaseOrderStatus, reason?: string) => void;
  updatePOMeta: (packetId: string, poId: string, patch: Partial<Pick<PurchaseOrder, "vendorOrderNumber" | "vendorContact" | "expectedDeliveryDate" | "paymentMethod" | "paymentTerms" | "notes">>) => void;
  addPOInvoice: (packetId: string, poId: string, invoice: POInvoiceLink) => void;
  updatePOInvoice: (packetId: string, poId: string, invoiceNumber: string, patch: Partial<POInvoiceLink>) => void;
  removePOInvoice: (packetId: string, poId: string, invoiceNumber: string) => void;
  attachReceiptToPO: (packetId: string, poId: string, receiptId: string) => void;
  detachReceiptFromPO: (packetId: string, poId: string, receiptId: string) => void;
  cancelPO: (packetId: string, poId: string, reason: string) => void;

  // Vendor lead-time configuration
  setVendorLeadTimes: React.Dispatch<React.SetStateAction<VendorLeadTimeConfig[]>>;
  upsertVendorLeadTime: (cfg: VendorLeadTimeConfig) => void;
  removeVendorLeadTime: (vendorName: string) => void;
}

const OrderingPacketContext = createContext<OrderingPacketContextValue | null>(null);

export function OrderingPacketProvider({ children }: { children: ReactNode }) {
  const [packets, setPackets] = useState<OrderingPacket[]>([]);
  const [vendorLeadTimes, setVendorLeadTimes] = useState<VendorLeadTimeConfig[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();
  const { role, roleInfo } = useRole();
  const actor = roleInfo?.label || role || "system";

  useEffect(() => {
    if (deferHeavyProvider) {
      setHydrated(false);
      return;
    }

    let alive = true;
    void (async () => {
      try {
        const organizationId = await getCurrentOrganizationId().catch(() => null);
        if (!organizationId) {
          if (alive) setHydrated(true);
          return;
        }
        const [packetRes, leadTimeRes] = await Promise.all([
          supabase
            .from("ordering_packets")
            .select("packet_id, payload")
            .eq("organization_id", organizationId)
            .order("updated_at", { ascending: false }),
          supabase
            .from("ordering_vendor_lead_times")
            .select("payload")
            .eq("organization_id", organizationId),
        ]);
        if (!alive) return;
        if (!packetRes.error && packetRes.data) {
          setPackets(
            packetRes.data
              .map((r: any) => r.payload as OrderingPacket)
              .filter((p) => p && typeof p.id === "string"),
          );
        }
        if (!leadTimeRes.error && leadTimeRes.data && leadTimeRes.data.length > 0) {
          setVendorLeadTimes(
            leadTimeRes.data
              .map((r: any) => r.payload as VendorLeadTimeConfig)
              .filter((v) => v && typeof v.vendorName === "string"),
          );
        }
      } catch (error) {
        console.warn("[orderingPacket] DB hydrate failed", error);
      } finally {
        if (alive) setHydrated(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [deferHeavyProvider]);

  useEffect(() => {
    if (deferHeavyProvider) return;
    if (!hydrated) return;
    void (async () => {
      try {
        const organizationId = await getCurrentOrganizationId().catch(() => null);
        if (!organizationId) return;
        if (packets.length === 0) return;
        const rows = packets.map((p) => ({
          organization_id: organizationId,
          packet_id: p.id,
          job_id: p.jobId || null,
          payload: p,
          updated_at: new Date().toISOString(),
        }));
        const { error } = await supabase
          .from("ordering_packets")
          .upsert(rows, { onConflict: "organization_id,packet_id" });
        if (error) console.warn("[orderingPacket] packets persist failed", error.message);
      } catch (error) {
        console.warn("[orderingPacket] packets persist threw", error);
      }
    })();
  }, [packets, hydrated, deferHeavyProvider]);

  useEffect(() => {
    if (deferHeavyProvider) return;
    if (!hydrated) return;
    void (async () => {
      try {
        const organizationId = await getCurrentOrganizationId().catch(() => null);
        if (!organizationId) return;
        if (vendorLeadTimes.length === 0) return;
        const rows = vendorLeadTimes.map((cfg) => ({
          organization_id: organizationId,
          vendor_name: cfg.vendorName,
          payload: cfg,
          updated_at: new Date().toISOString(),
        }));
        const { error } = await supabase
          .from("ordering_vendor_lead_times")
          .upsert(rows, { onConflict: "organization_id,vendor_name" });
        if (error) console.warn("[orderingPacket] lead times persist failed", error.message);
      } catch (error) {
        console.warn("[orderingPacket] lead times persist threw", error);
      }
    })();
  }, [vendorLeadTimes, hydrated, deferHeavyProvider]);

  const appendAudit = useCallback(
    (packetId: string, ev: Omit<OrderingAuditEvent, "id" | "packetId" | "at" | "actor">) => {
      setPackets((prev) =>
        prev.map((p) =>
          p.id === packetId
            ? recomputeTotals({
                ...p,
                auditEvents: [
                  ...p.auditEvents,
                  { id: uid("aud"), packetId, at: new Date().toISOString(), actor, ...ev },
                ],
              })
            : p,
        ),
      );
    },
    [actor],
  );

  const getPacket = useCallback((id: string) => packets.find((p) => p.id === id), [packets]);
  const getPacketsByJob = useCallback((jobId: string) => packets.filter((p) => p.jobId === jobId), [packets]);
  const getPacketsByStatus = useCallback(
    (status: OrderingPacketStatus) => packets.filter((p) => p.status === status),
    [packets],
  );

  const createPacket = useCallback(
    ({ job, intendedVendor, orderType, notes, sentBy }: CreatePacketInput): OrderingPacket => {
      const bom = getJobBom(job);
      const normalizedIntendedVendor = normalizeVendorCandidate(intendedVendor);
      const lineItems: OrderingLineItem[] = bom.items.map((item) => {
        const sku = SEED_SKU_CATALOG.find((s) => s.sku === item.sku);
        const unitPrice = sku?.unitCost ?? 0;
        const qty = item.quantity ?? 1;
        const seededVendor = normalizeVendorCandidate(sku?.primaryVendor?.name);
        const inferredVendor = inferVendorFromSkuOrName(item.sku, item.name ?? "");
        const vendor = seededVendor || normalizeVendorCandidate(normalizedIntendedVendor) || inferredVendor || "Unassigned";
        const projectedNameCandidate = String(item.name ?? "").trim();
        const projectedItemName = !isUuidLike(projectedNameCandidate)
          ? projectedNameCandidate
          : (sku?.itemName ?? item.sku);
        const exceptions: OrderingLineItem["exceptions"] = [];
        if (unitPrice === 0) {
          exceptions.push({
            kind: "no_price",
            detail: "BOM has no projected unit price — confirm with vendor.",
            raisedAt: new Date().toISOString(),
            raisedBy: "system",
            resolved: false,
          });
        }
        return {
          id: uid("li"),
          projectedSku: item.sku,
          projectedItemName,
          projectedVendor: vendor,
          projectedQty: qty,
          projectedUnitPrice: unitPrice,
          projectedLineCost: unitPrice * qty,
          category: sku?.category,
          substituted: false,
          vendorChanged: false,
          status: "pending",
          exceptions,
        };
      });

      const projectedTotal = lineItems.reduce((s, l) => s + l.projectedLineCost, 0);
      const exceptionCount = lineItems.reduce(
        (n, l) => n + l.exceptions.filter((e) => !e.resolved).length, 0,
      );
      const initialStatus: OrderingPacketStatus = exceptionCount > 0 ? "needs_review" : "draft";

      const packet: OrderingPacket = {
        id: uid("pkt"),
        jobId: job.id,
        jobLabel: `${job.customerName} – ${job.projectType}`,
        customerName: job.customerName,
        intendedVendor,
        orderType,
        notes,
        sentBy,
        sentAt: new Date().toISOString(),
        status: initialStatus,
        lineItems,
        projectedTotal,
        actualTotal: 0,
        auditEvents: [{
          id: uid("aud"),
          packetId: "", // backfilled below
          kind: "packet_created",
          at: new Date().toISOString(),
          actor: sentBy,
          summary: `Packet sent to ordering — ${lineItems.length} line${lineItems.length === 1 ? "" : "s"}, intended vendor: ${intendedVendor} (${orderType})`,
        }],
        exceptionCount,
      };
      // Backfill packetId on the seed audit event
      packet.auditEvents[0].packetId = packet.id;

      setPackets((prev) => [...prev, packet]);
      return packet;
    },
    [],
  );

  const updatePacketStatus = useCallback(
    (id: string, status: OrderingPacketStatus, reason?: string) => {
      setPackets((prev) => {
        const existing = prev.find((p) => p.id === id);
        if (!existing || existing.status === status) return prev;
        return prev.map((p) =>
          p.id === id
            ? recomputeTotals({
                ...p,
                status,
                auditEvents: [...p.auditEvents, {
                  id: uid("aud"), packetId: id, at: new Date().toISOString(), actor,
                  kind: "status_changed",
                  summary: `Status: ${existing.status} → ${status}${reason ? ` (${reason})` : ""}`,
                  oldValue: existing.status, newValue: status, reason,
                }],
              })
            : p,
        );
      });
    },
    [actor],
  );

  const updatePacketMeta: OrderingPacketContextValue["updatePacketMeta"] = useCallback(
    (id, patch) => {
      setPackets((prev) =>
        prev.map((p) => (p.id === id ? recomputeTotals({ ...p, ...patch }) : p)),
      );
    },
    [],
  );

  const updateLineItem: OrderingPacketContextValue["updateLineItem"] = useCallback(
    (packetId, lineId, patch, auditOptions) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const idx = p.lineItems.findIndex((l) => l.id === lineId);
          if (idx === -1) return p;
          const before = p.lineItems[idx];
          const merged: OrderingLineItem = { ...before, ...patch };
          // Recompute actualLineCost from qty × unitPrice if either changed
          if ("actualQty" in patch || "actualUnitPrice" in patch || "actualTax" in patch || "actualShipping" in patch || "actualFees" in patch) {
            merged.actualLineCost = computeLineCost(merged);
          }
          // Auto-flag substitution / vendor change
          if (patch.actualSku && patch.actualSku !== before.projectedSku) merged.substituted = true;
          if (patch.actualVendor && patch.actualVendor !== before.projectedVendor) merged.vendorChanged = true;

          // Auto-resolve `no_price` exception once a real price is entered.
          // Lets reps fix BOM gaps inline without a separate "resolve" click.
          if (patch.actualUnitPrice != null && patch.actualUnitPrice > 0) {
            merged.exceptions = merged.exceptions.map((e) =>
              e.kind === "no_price" && !e.resolved
                ? { ...e, resolved: true, resolvedAt: new Date().toISOString(), resolvedBy: actor }
                : e,
            );
          }

          const lineItems = [...p.lineItems];
          lineItems[idx] = merged;

          // Build audit events for material changes
          const newEvents: OrderingAuditEvent[] = [];
          const baseEv = { packetId, lineItemId: lineId, at: new Date().toISOString(), actor };
          const lineLabel = before.projectedItemName;

          if (patch.actualUnitPrice != null && patch.actualUnitPrice !== before.actualUnitPrice) {
            const oldPrice = before.actualUnitPrice ?? before.projectedUnitPrice;
            const variance = patch.actualUnitPrice - oldPrice;
            newEvents.push({
              ...baseEv, id: uid("aud"),
              kind: auditOptions?.isOverride ? "price_override" : "price_edited",
              summary: `${lineLabel}: unit price ${oldPrice ? `$${oldPrice.toLocaleString()}` : "—"} → $${patch.actualUnitPrice.toLocaleString()}${auditOptions?.reason ? ` — ${auditOptions.reason}` : ""}`,
              oldValue: oldPrice, newValue: patch.actualUnitPrice,
              varianceAmount: variance, reason: auditOptions?.reason,
            });
          }
          if (patch.actualQty != null && patch.actualQty !== before.actualQty) {
            newEvents.push({
              ...baseEv, id: uid("aud"),
              kind: "qty_edited",
              summary: `${lineLabel}: qty ${before.actualQty ?? before.projectedQty} → ${patch.actualQty}`,
              oldValue: before.actualQty ?? before.projectedQty, newValue: patch.actualQty,
              reason: auditOptions?.reason,
            });
          }
          if (patch.actualVendor && patch.actualVendor !== before.actualVendor) {
            newEvents.push({
              ...baseEv, id: uid("aud"),
              kind: "vendor_changed",
              summary: `${lineLabel}: vendor → ${patch.actualVendor}${auditOptions?.reason ? ` — ${auditOptions.reason}` : ""}`,
              oldValue: before.actualVendor ?? before.projectedVendor,
              newValue: patch.actualVendor, reason: auditOptions?.reason,
            });
          }
          if (patch.actualSku && patch.actualSku !== before.actualSku) {
            newEvents.push({
              ...baseEv, id: uid("aud"),
              kind: "line_substituted",
              summary: `${lineLabel}: SKU ${before.projectedSku} → ${patch.actualSku}${auditOptions?.reason ? ` — ${auditOptions.reason}` : ""}`,
              oldValue: before.projectedSku, newValue: patch.actualSku, reason: auditOptions?.reason,
            });
          }

          return recomputeTotals({
            ...p,
            lineItems,
            auditEvents: [...p.auditEvents, ...newEvents],
          });
        }),
      );
    },
    [actor],
  );

  const setLineStatus = useCallback(
    (packetId: string, lineId: string, status: OrderingLineStatus, reason?: string) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const lineItems = p.lineItems.map((l) =>
            l.id === lineId
              ? { ...l, status, ...(status === "received" ? { receivedAt: new Date().toISOString() } : {}) }
              : l,
          );
          return recomputeTotals({
            ...p, lineItems,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, lineItemId: lineId, at: new Date().toISOString(), actor,
              kind: "status_changed",
              summary: `Line status → ${status}${reason ? ` (${reason})` : ""}`,
              reason,
            }],
          });
        }),
      );
    },
    [actor],
  );

  const addLineException: OrderingPacketContextValue["addLineException"] = useCallback(
    (packetId, lineId, kind, detail) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const lineItems = p.lineItems.map((l) =>
            l.id === lineId
              ? {
                  ...l,
                  exceptions: [...l.exceptions, {
                    kind, detail, raisedAt: new Date().toISOString(), raisedBy: actor, resolved: false,
                  }],
                }
              : l,
          );
          return recomputeTotals({
            ...p, lineItems,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, lineItemId: lineId, at: new Date().toISOString(), actor,
              kind: "exception_raised",
              summary: `Exception (${kind}): ${detail}`,
            }],
          });
        }),
      );
    },
    [actor],
  );

  const resolveLineException: OrderingPacketContextValue["resolveLineException"] = useCallback(
    (packetId, lineId, exceptionIndex) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const lineItems = p.lineItems.map((l) => {
            if (l.id !== lineId) return l;
            const exceptions = l.exceptions.map((e, i) =>
              i === exceptionIndex ? { ...e, resolved: true, resolvedAt: new Date().toISOString(), resolvedBy: actor } : e,
            );
            return { ...l, exceptions };
          });
          return recomputeTotals({
            ...p, lineItems,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, lineItemId: lineId, at: new Date().toISOString(), actor,
              kind: "exception_resolved",
              summary: `Resolved exception #${exceptionIndex + 1}`,
            }],
          });
        }),
      );
    },
    [actor],
  );

  const markPlaced = useCallback(
    (packetId: string) => {
      setPackets((prev) => {
        // Compute global PO sequence for the day across all packets
        const today = new Date();
        const y = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const todayPrefix = `PO-${y}-${mm}${dd}-`;
        const usedToday = new Set<string>();
        prev.forEach((pp) => (pp.purchaseOrders ?? []).forEach((po) => {
          if (po.poNumber.startsWith(todayPrefix)) usedToday.add(po.poNumber);
        }));
        let nextSeq = usedToday.size + 1;

        return prev.map((p) => {
          if (p.id !== packetId) return p;
          const allOrdered = p.lineItems.every((l) => l.status === "ordered" || l.status === "received");
          const someOrdered = p.lineItems.some((l) => l.status === "ordered" || l.status === "received");
          const status: OrderingPacketStatus = allOrdered ? "placed" : someOrdered ? "partially_placed" : p.status;

          // Auto-generate POs grouped by vendor for any lines not yet on a PO
          const linesByVendor = new Map<string, OrderingLineItem[]>();
          p.lineItems.forEach((l) => {
            if (l.status === "cancelled") return;
            const vendor = resolveEffectiveVendor(l, p.intendedVendor);
            if (!linesByVendor.has(vendor)) linesByVendor.set(vendor, []);
            linesByVendor.get(vendor)!.push(l);
          });

          const existingPOs = p.purchaseOrders ?? [];
          const existingByVendor = new Map(existingPOs.map((po) => [po.vendorName, po]));
          const newPOList: PurchaseOrder[] = [];
          const lineToPo = new Map<string, string>();
          const newAuditEvents: OrderingAuditEvent[] = [];

          linesByVendor.forEach((vendorLines, vendor) => {
            const subtotal = vendorLines.reduce((s, l) => {
              const cost = l.actualLineCost ?? (l.actualUnitPrice != null
                ? (l.actualQty ?? l.projectedQty) * l.actualUnitPrice
                : l.projectedLineCost);
              return s + cost;
            }, 0);
            const existing = existingByVendor.get(vendor);
            if (existing) {
              const updated: PurchaseOrder = {
                ...existing,
                subtotal,
                status: existing.status === "draft" ? "sent" : existing.status,
                ...(existing.status === "draft" && !existing.sentAt
                  ? { sentAt: new Date().toISOString(), sentBy: actor } : {}),
              };
              newPOList.push(updated);
              vendorLines.forEach((l) => lineToPo.set(l.id, updated.id));
            } else {
              const poNumber = `${todayPrefix}${String(nextSeq++).padStart(3, "0")}`;
              const po: PurchaseOrder = {
                id: uid("po"),
                packetId: p.id,
                jobId: p.jobId,
                jobLabel: p.jobLabel,
                customerName: p.customerName,
                poNumber,
                vendorName: vendor,
                status: "sent",
                subtotal,
                invoices: [],
                receiptIds: [],
                paymentMethod: p.paymentMethod,
                paymentTerms: p.paymentTerms,
                expectedDeliveryDate: p.expectedDeliveryDate,
                createdAt: new Date().toISOString(),
                createdBy: actor,
                sentAt: new Date().toISOString(),
                sentBy: actor,
              };
              newPOList.push(po);
              vendorLines.forEach((l) => lineToPo.set(l.id, po.id));
              newAuditEvents.push({
                id: uid("aud"), packetId: p.id, poId: po.id, at: new Date().toISOString(), actor,
                kind: "po_created",
                summary: `${poNumber} created — ${vendor} · $${subtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })} · ${vendorLines.length} line${vendorLines.length === 1 ? "" : "s"}`,
              });
            }
          });
          existingPOs.forEach((po) => {
            if (!linesByVendor.has(po.vendorName) && !newPOList.some((n) => n.id === po.id)) {
              newPOList.push(po);
            }
          });

          const lineItems = p.lineItems.map((l) => {
            const poId = lineToPo.get(l.id);
            return poId ? { ...l, poId } : l;
          });

          return recomputeTotals({
            ...p, status, lineItems,
            purchaseOrders: newPOList,
            placedAt: new Date().toISOString(),
            placedBy: actor,
            auditEvents: [...p.auditEvents, ...newAuditEvents, {
              id: uid("aud"), packetId, at: new Date().toISOString(), actor,
              kind: "marked_placed",
              summary: allOrdered ? "Order placed (all lines)" : "Order partially placed",
            }],
          });
        });
      });
    },
    [actor],
  );

  const markReceived = useCallback(
    (packetId: string) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const lineItems = p.lineItems.map((l) =>
            l.status === "ordered"
              ? { ...l, status: "received" as OrderingLineStatus, receivedAt: new Date().toISOString() }
              : l,
          );
          // Bump matching POs to fully_received (unless invoiced/paid/closed/cancelled)
          const purchaseOrders = (p.purchaseOrders ?? []).map((po) => {
            if (po.status === "invoiced" || po.status === "paid" || po.status === "closed" || po.status === "cancelled") return po;
            const poLines = lineItems.filter((l) => l.poId === po.id);
            if (poLines.length === 0) return po;
            const allReceived = poLines.every((l) => l.status === "received");
            const someReceived = poLines.some((l) => l.status === "received");
            const nextStatus: PurchaseOrderStatus = allReceived ? "fully_received" : someReceived ? "partially_received" : po.status;
            return nextStatus !== po.status ? { ...po, status: nextStatus } : po;
          });
          return recomputeTotals({
            ...p, lineItems, purchaseOrders,
            status: "received",
            receivedAt: new Date().toISOString(),
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, at: new Date().toISOString(), actor,
              kind: "marked_received",
              summary: "Packet marked received / intake complete",
            }],
          });
        }),
      );
    },
    [actor],
  );

  const cancelPacket = useCallback(
    (packetId: string, reason: string) => {
      setPackets((prev) =>
        prev.map((p) =>
          p.id === packetId
            ? recomputeTotals({
                ...p,
                status: "cancelled",
                cancelledAt: new Date().toISOString(),
                cancellationReason: reason,
                auditEvents: [...p.auditEvents, {
                  id: uid("aud"), packetId, at: new Date().toISOString(), actor,
                  kind: "cancelled",
                  summary: `Packet cancelled — ${reason}`,
                  reason,
                }],
              })
            : p,
        ),
      );
    },
    [actor],
  );

  const upsertVendorLeadTime = useCallback((cfg: VendorLeadTimeConfig) => {
    setVendorLeadTimes((prev) => {
      const idx = prev.findIndex((v) => v.vendorName.toLowerCase() === cfg.vendorName.toLowerCase());
      if (idx === -1) return [...prev, cfg];
      const next = [...prev]; next[idx] = cfg; return next;
    });
  }, []);

  const removeVendorLeadTime = useCallback((vendorName: string) => {
    setVendorLeadTimes((prev) => prev.filter((v) => v.vendorName !== vendorName));
  }, []);

  /* ── Vendor receipts (reconciliation) ──────────────────────────────────── */

  const createReceipt: OrderingPacketContextValue["createReceipt"] = useCallback(
    (packetId, init) => {
      let created: VendorReceipt | undefined;
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const receipt: VendorReceipt = {
            id: uid("rcpt"),
            packetId,
            vendorName: init.vendorName ?? p.intendedVendor ?? "",
            invoiceNumber: init.invoiceNumber ?? "",
            invoiceDate: init.invoiceDate ?? new Date().toISOString().slice(0, 10),
            invoiceTotal: init.invoiceTotal ?? 0,
            fees: { freight: 0, tax: 0, processing: 0, other: 0 },
            locked: false,
            createdAt: new Date().toISOString(),
            createdBy: actor,
          };
          created = receipt;
          return recomputeTotals({
            ...p,
            receipts: [...(p.receipts ?? []), receipt],
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, receiptId: receipt.id, at: new Date().toISOString(), actor,
              kind: "receipt_created",
              summary: `Receipt created — ${receipt.vendorName}${receipt.invoiceNumber ? ` #${receipt.invoiceNumber}` : ""}`,
            }],
          });
        }),
      );
      return created;
    },
    [actor],
  );

  const updateReceipt: OrderingPacketContextValue["updateReceipt"] = useCallback(
    (packetId, receiptId, patch) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const receipts = (p.receipts ?? []).map((r) => {
            if (r.id !== receiptId) return r;
            if (r.locked) return r; // immutable while locked
            return { ...r, ...patch, fees: { ...r.fees, ...(patch.fees ?? {}) } };
          });
          return recomputeTotals({
            ...p, receipts,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, receiptId, at: new Date().toISOString(), actor,
              kind: "receipt_updated",
              summary: `Receipt updated`,
            }],
          });
        }),
      );
    },
    [actor],
  );

  const assignLineToReceipt: OrderingPacketContextValue["assignLineToReceipt"] = useCallback(
    (packetId, lineId, receiptId) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          // Don't allow reassigning lines that belong to a locked receipt
          const targetReceipt = receiptId ? (p.receipts ?? []).find((r) => r.id === receiptId) : null;
          if (targetReceipt?.locked) return p;
          const lineItems = p.lineItems.map((l) => {
            if (l.id !== lineId) return l;
            // If the line currently belongs to a locked receipt, skip
            if (l.receiptId) {
              const current = (p.receipts ?? []).find((r) => r.id === l.receiptId);
              if (current?.locked) return l;
            }
            return { ...l, receiptId: receiptId ?? undefined };
          });
          return recomputeTotals({ ...p, lineItems });
        }),
      );
    },
    [],
  );

  const lockReceipt: OrderingPacketContextValue["lockReceipt"] = useCallback(
    (packetId, receiptId) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const receipts = (p.receipts ?? []).map((r) =>
            r.id === receiptId
              ? { ...r, locked: true, lockedAt: new Date().toISOString(), lockedBy: actor }
              : r,
          );
          return recomputeTotals({
            ...p, receipts,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, receiptId, at: new Date().toISOString(), actor,
              kind: "receipt_locked",
              summary: `Receipt locked — variance reconciled to $0.00`,
            }],
          });
        }),
      );
    },
    [actor],
  );

  const unlockReceipt: OrderingPacketContextValue["unlockReceipt"] = useCallback(
    (packetId, receiptId, reason) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const receipts = (p.receipts ?? []).map((r) =>
            r.id === receiptId
              ? { ...r, locked: false, lockedAt: undefined, lockedBy: undefined }
              : r,
          );
          return recomputeTotals({
            ...p, receipts,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, receiptId, at: new Date().toISOString(), actor,
              kind: "receipt_unlocked",
              summary: `Receipt unlocked — ${reason}`,
              reason,
            }],
          });
        }),
      );
    },
    [actor],
  );

  const deleteReceipt: OrderingPacketContextValue["deleteReceipt"] = useCallback(
    (packetId, receiptId) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const target = (p.receipts ?? []).find((r) => r.id === receiptId);
          if (target?.locked) return p; // can't delete a locked receipt
          const receipts = (p.receipts ?? []).filter((r) => r.id !== receiptId);
          // Unassign any lines pointing at it
          const lineItems = p.lineItems.map((l) =>
            l.receiptId === receiptId ? { ...l, receiptId: undefined } : l,
          );
          return recomputeTotals({
            ...p, receipts, lineItems,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, receiptId, at: new Date().toISOString(), actor,
              kind: "receipt_deleted",
              summary: `Receipt deleted`,
            }],
          });
        }),
      );
    },
    [actor],
  );


  /* ── Purchase Orders ───────────────────────────────────────────────────── */

  const computePOSubtotal = (lines: OrderingLineItem[]): number =>
    lines.reduce((s, l) => {
      const cost = l.actualLineCost ?? (l.actualUnitPrice != null
        ? (l.actualQty ?? l.projectedQty) * l.actualUnitPrice
        : l.projectedLineCost);
      return s + cost;
    }, 0);

  const formatPONumber = (date: Date, seq: number): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `PO-${y}-${m}${d}-${String(seq).padStart(3, "0")}`;
  };

  const generatePOsForPacket: OrderingPacketContextValue["generatePOsForPacket"] = useCallback(
    (packetId) => {
      let result: PurchaseOrder[] = [];
      setPackets((prev) => {
        // Compute global PO sequence for the day across all packets
        const today = new Date();
        const todayPrefix = formatPONumber(today, 0).slice(0, -3); // PO-YYYY-MMDD-
        const usedToday = new Set<string>();
        prev.forEach((p) => {
          (p.purchaseOrders ?? []).forEach((po) => {
            if (po.poNumber.startsWith(todayPrefix)) usedToday.add(po.poNumber);
          });
        });
        let nextSeq = usedToday.size + 1;

        return prev.map((p) => {
          if (p.id !== packetId) return p;

          // Group lines by their effective vendor
          const linesByVendor = new Map<string, OrderingLineItem[]>();
          p.lineItems.forEach((l) => {
            if (l.status === "cancelled") return;
            const vendor = resolveEffectiveVendor(l, p.intendedVendor);
            if (!linesByVendor.has(vendor)) linesByVendor.set(vendor, []);
            linesByVendor.get(vendor)!.push(l);
          });

          const existingPOs = p.purchaseOrders ?? [];
          const existingByVendor = new Map(existingPOs.map((po) => [po.vendorName, po]));
          const newPOs: PurchaseOrder[] = [];
          const updatedLineIds = new Map<string, string>(); // lineId → poId
          const newAuditEvents: OrderingAuditEvent[] = [];

          linesByVendor.forEach((vendorLines, vendor) => {
            const existing = existingByVendor.get(vendor);
            const subtotal = computePOSubtotal(vendorLines);
            if (existing) {
              // Refresh subtotal & line membership
              const updated = { ...existing, subtotal };
              newPOs.push(updated);
              vendorLines.forEach((l) => updatedLineIds.set(l.id, updated.id));
            } else {
              const poNumber = formatPONumber(today, nextSeq++);
              const po: PurchaseOrder = {
                id: uid("po"),
                packetId: p.id,
                jobId: p.jobId,
                jobLabel: p.jobLabel,
                customerName: p.customerName,
                poNumber,
                vendorName: vendor,
                status: "draft",
                subtotal,
                invoices: [],
                receiptIds: [],
                paymentMethod: p.paymentMethod,
                paymentTerms: p.paymentTerms,
                expectedDeliveryDate: p.expectedDeliveryDate,
                createdAt: new Date().toISOString(),
                createdBy: actor,
              };
              newPOs.push(po);
              vendorLines.forEach((l) => updatedLineIds.set(l.id, po.id));
              newAuditEvents.push({
                id: uid("aud"), packetId: p.id, poId: po.id, at: new Date().toISOString(), actor,
                kind: "po_created",
                summary: `${poNumber} created — ${vendor} · $${subtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })} · ${vendorLines.length} line${vendorLines.length === 1 ? "" : "s"}`,
              });
            }
          });

          // Preserve any existing POs that no longer have lines (e.g. all reassigned away)
          existingPOs.forEach((po) => {
            if (!linesByVendor.has(po.vendorName) && !newPOs.some((n) => n.id === po.id)) {
              newPOs.push({ ...po, subtotal: 0 });
            }
          });

          result = newPOs;
          const lineItems = p.lineItems.map((l) => {
            const poId = updatedLineIds.get(l.id);
            return poId ? { ...l, poId } : l;
          });

          return recomputeTotals({
            ...p,
            purchaseOrders: newPOs,
            lineItems,
            auditEvents: [...p.auditEvents, ...newAuditEvents],
          });
        });
      });
      return result;
    },
    [actor],
  );

  const getAllPurchaseOrders = useCallback(
    () => packets.flatMap((p) => p.purchaseOrders ?? []),
    [packets],
  );

  const getPurchaseOrder = useCallback(
    (poId: string) => {
      for (const p of packets) {
        const po = (p.purchaseOrders ?? []).find((x) => x.id === poId);
        if (po) return { po, packet: p };
      }
      return undefined;
    },
    [packets],
  );

  const updatePOStatus: OrderingPacketContextValue["updatePOStatus"] = useCallback(
    (packetId, poId, status, reason) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const pos = (p.purchaseOrders ?? []).map((po) => {
            if (po.id !== poId || po.status === status) return po;
            const next: PurchaseOrder = {
              ...po,
              status,
              ...(status === "sent" && !po.sentAt ? { sentAt: new Date().toISOString(), sentBy: actor } : {}),
              ...(status === "closed" ? { closedAt: new Date().toISOString() } : {}),
              ...(status === "cancelled" ? { cancelledAt: new Date().toISOString(), cancellationReason: reason } : {}),
            };
            return next;
          });
          const target = pos.find((po) => po.id === poId);
          return recomputeTotals({
            ...p,
            purchaseOrders: pos,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, poId, at: new Date().toISOString(), actor,
              kind: status === "sent" ? "po_sent" : status === "cancelled" ? "po_cancelled" : "po_status_changed",
              summary: `${target?.poNumber ?? "PO"} status → ${status}${reason ? ` (${reason})` : ""}`,
              reason,
            }],
          });
        }),
      );
    },
    [actor],
  );

  const updatePOMeta: OrderingPacketContextValue["updatePOMeta"] = useCallback(
    (packetId, poId, patch) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const pos = (p.purchaseOrders ?? []).map((po) => po.id === poId ? { ...po, ...patch } : po);
          return recomputeTotals({
            ...p, purchaseOrders: pos,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, poId, at: new Date().toISOString(), actor,
              kind: "po_updated",
              summary: `PO updated`,
            }],
          });
        }),
      );
    },
    [actor],
  );

  const addPOInvoice: OrderingPacketContextValue["addPOInvoice"] = useCallback(
    (packetId, poId, invoice) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const pos = (p.purchaseOrders ?? []).map((po) => {
            if (po.id !== poId) return po;
            // Dedupe by invoice number
            const filtered = po.invoices.filter((i) => i.invoiceNumber !== invoice.invoiceNumber);
            const next: PurchaseOrder = {
              ...po,
              invoices: [...filtered, invoice],
              status: po.status === "fully_received" || po.status === "partially_received"
                ? "invoiced" : po.status,
            };
            return next;
          });
          const target = pos.find((po) => po.id === poId);
          return recomputeTotals({
            ...p, purchaseOrders: pos,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, poId, at: new Date().toISOString(), actor,
              kind: "po_invoice_linked",
              summary: `${target?.poNumber ?? "PO"}: invoice #${invoice.invoiceNumber} attached — $${invoice.invoiceAmount.toLocaleString()}`,
            }],
          });
        }),
      );
    },
    [actor],
  );

  const updatePOInvoice: OrderingPacketContextValue["updatePOInvoice"] = useCallback(
    (packetId, poId, invoiceNumber, patch) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const pos = (p.purchaseOrders ?? []).map((po) => {
            if (po.id !== poId) return po;
            const invoices = po.invoices.map((i) => i.invoiceNumber === invoiceNumber ? { ...i, ...patch } : i);
            const allPaid = invoices.length > 0 && invoices.every((i) => i.paid);
            const next: PurchaseOrder = {
              ...po,
              invoices,
              status: allPaid && po.status !== "closed" && po.status !== "cancelled" ? "paid" : po.status,
            };
            return next;
          });
          return recomputeTotals({
            ...p, purchaseOrders: pos,
            auditEvents: [...p.auditEvents, {
              id: uid("aud"), packetId, poId, at: new Date().toISOString(), actor,
              kind: patch.paid ? "po_payment_recorded" : "po_updated",
              summary: patch.paid
                ? `Invoice #${invoiceNumber} marked paid${patch.paymentReference ? ` (${patch.paymentReference})` : ""}`
                : `Invoice #${invoiceNumber} updated`,
            }],
          });
        }),
      );
    },
    [actor],
  );

  const removePOInvoice: OrderingPacketContextValue["removePOInvoice"] = useCallback(
    (packetId, poId, invoiceNumber) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const pos = (p.purchaseOrders ?? []).map((po) =>
            po.id === poId ? { ...po, invoices: po.invoices.filter((i) => i.invoiceNumber !== invoiceNumber) } : po
          );
          return recomputeTotals({ ...p, purchaseOrders: pos });
        }),
      );
    },
    [],
  );

  const attachReceiptToPO: OrderingPacketContextValue["attachReceiptToPO"] = useCallback(
    (packetId, poId, receiptId) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const pos = (p.purchaseOrders ?? []).map((po) =>
            po.id === poId && !po.receiptIds.includes(receiptId)
              ? { ...po, receiptIds: [...po.receiptIds, receiptId] }
              : po,
          );
          return recomputeTotals({ ...p, purchaseOrders: pos });
        }),
      );
    },
    [],
  );

  const detachReceiptFromPO: OrderingPacketContextValue["detachReceiptFromPO"] = useCallback(
    (packetId, poId, receiptId) => {
      setPackets((prev) =>
        prev.map((p) => {
          if (p.id !== packetId) return p;
          const pos = (p.purchaseOrders ?? []).map((po) =>
            po.id === poId ? { ...po, receiptIds: po.receiptIds.filter((id) => id !== receiptId) } : po,
          );
          return recomputeTotals({ ...p, purchaseOrders: pos });
        }),
      );
    },
    [],
  );

  const cancelPO: OrderingPacketContextValue["cancelPO"] = useCallback(
    (packetId, poId, reason) => {
      updatePOStatus(packetId, poId, "cancelled", reason);
    },
    [updatePOStatus],
  );

  const value = useMemo<OrderingPacketContextValue>(() => ({
    packets, vendorLeadTimes,
    getPacket, getPacketsByJob, getPacketsByStatus,
    createPacket, updatePacketStatus, updatePacketMeta,
    updateLineItem, setLineStatus, addLineException, resolveLineException,
    markPlaced, markReceived, cancelPacket,
    createReceipt, updateReceipt, assignLineToReceipt, lockReceipt, unlockReceipt, deleteReceipt,
    generatePOsForPacket, getAllPurchaseOrders, getPurchaseOrder,
    updatePOStatus, updatePOMeta, addPOInvoice, updatePOInvoice, removePOInvoice,
    attachReceiptToPO, detachReceiptFromPO, cancelPO,
    setVendorLeadTimes, upsertVendorLeadTime, removeVendorLeadTime,
  }), [
    packets, vendorLeadTimes,
    getPacket, getPacketsByJob, getPacketsByStatus,
    createPacket, updatePacketStatus, updatePacketMeta,
    updateLineItem, setLineStatus, addLineException, resolveLineException,
    markPlaced, markReceived, cancelPacket,
    createReceipt, updateReceipt, assignLineToReceipt, lockReceipt, unlockReceipt, deleteReceipt,
    generatePOsForPacket, getAllPurchaseOrders, getPurchaseOrder,
    updatePOStatus, updatePOMeta, addPOInvoice, updatePOInvoice, removePOInvoice,
    attachReceiptToPO, detachReceiptFromPO, cancelPO,
    upsertVendorLeadTime, removeVendorLeadTime,
  ]);

  return <OrderingPacketContext.Provider value={value}>{children}</OrderingPacketContext.Provider>;
}

export function useOrderingPacket() {
  const ctx = useContext(OrderingPacketContext);
  if (!ctx) throw new Error("useOrderingPacket must be used within OrderingPacketProvider");
  return ctx;
}
